import * as React from 'react';

import {
    ExternalDocumentEditor,
    IExternalDocumentViewModel,
} from '../document-upload/ExternalDocumentEditor';
import { IDocumentModel, IDocumentRequestModel } from '../../../interfaces/ICase';
import { Modal, Stack } from 'react-bootstrap';
import { showErrorToast, showSuccessToast } from '../../../shared/ToastUtils';

import { IValidation } from '../../../interfaces/IError';
import { IconButton } from '../../../shared/buttons/IconButton';
import { LoadingButton } from '../../../shared/buttons/LoadingButton';
import { useAddExternalDocumentMutation } from './useAddExternalDocumentMutation';
import { v4 as uuidv4 } from 'uuid';

export interface ILinkToExternalDocumentModalProps {
    documentTypeId: number;
    documentSubTypeId?: number;
    entityGuid: string;
    entityTypeId: number;
    existingDocuments?: IDocumentModel[];
    noteTypeId?: number;
    open: boolean;
    onHide: () => void;
    onSaveComplete: (documents: IDocumentModel[]) => void;
    strategyTypeId?: number;
}

const generateEditDocument = () => {
    return {
        isComplete: false,
        editGuid: uuidv4(),
        title: '',
        externalURL: '',
    } as IExternalDocumentViewModel;
};

export function LinkToExternalDocumentModal(props: ILinkToExternalDocumentModalProps) {
    const saveExternalDocumentsMutation = useAddExternalDocumentMutation();

    const [documents, setDocuments] = React.useState<IExternalDocumentViewModel[]>([
        generateEditDocument(),
    ]);

    const [validation, setValidation] = React.useState<IValidation>({});

    const documentCount = documents?.length ?? 0;
    const noun = documentCount > 1 || documentCount === 0 ? 'Documents' : 'Document';

    const handleUploadDocumentsClick = async () => {
        const request: IDocumentRequestModel = {
            documentModels: documents.map((doc) => ({
                ...doc,
                documentTypeId: props.documentTypeId,
                documentSubTypeId: props.documentSubTypeId,
            })),
            entityTypeId: props.entityTypeId,
            entityGuid: props.entityGuid,
            strategyTypeId: props.strategyTypeId,
            noteTypeId: props.noteTypeId,
        };
        saveExternalDocumentsMutation
            .mutateAsync(request)
            .then((resp) => {
                const createdDocuments = resp.payload?.data ?? [];
                showSuccessToast(`${documents?.length ?? 0} ${noun} uploaded successfully`);
                props.onSaveComplete(createdDocuments);
            })
            .catch(() => showErrorToast('Failed to add external documents'));
    };

    const handleCancelClick = () => {
        props.onHide();
    };

    const handleAddAnotherLinkClick = () => {
        setDocuments((prev) => [...prev, generateEditDocument()]);
    };

    const handleRemoveClick = (deletedDoc: IExternalDocumentViewModel) => {
        setDocuments((prev) => prev.filter((doc) => doc.editGuid !== deletedDoc.editGuid));
    };

    const handleDocumentChange = (changedDoc: IExternalDocumentViewModel) => {
        setDocuments((prev) =>
            prev.map((doc) =>
                doc.editGuid === changedDoc.editGuid ? { ...doc, ...changedDoc } : doc
            )
        );
    };

    const hasDocumentErrors = documents.some((doc) => !doc.isValid);

    return (
        <Modal centered show={props.open} size="lg" onHide={props.onHide} backdrop>
            <Modal.Header>
                <Modal.Title>Link External Documents</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {validation.model && <div className="text-danger mb-2">{validation.model}</div>}

                <ul>
                    {documents?.map((doc) => (
                        <li
                            key={doc.guid}
                            style={{
                                background: '#f6f6f6',
                                padding: '1em',
                                marginTop: '1em',
                            }}
                        >
                            <div className="w-100">
                                <ExternalDocumentEditor
                                    document={doc}
                                    onExternalDocumentChange={handleDocumentChange}
                                    removeIconComponent={
                                        <IconButton
                                            buttonCssClass="btn-no-bg"
                                            icon="fal fa-lg fa-times"
                                            onClick={() => handleRemoveClick(doc)}
                                        />
                                    }
                                />
                            </div>
                        </li>
                    ))}
                </ul>
                <div className="container-fluid">
                    <div className="row">
                        <Stack direction="horizontal" gap={1}>
                            <i className="fal fa-plus" />
                            <span className="link" onClick={handleAddAnotherLinkClick}>
                                Add another Document
                            </span>
                        </Stack>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <LoadingButton
                    loading={saveExternalDocumentsMutation.isLoading}
                    loadingText="Linking..."
                    onClick={handleUploadDocumentsClick}
                    text={`Link ${noun}`}
                    variant="primary"
                    icon="fal fa-link"
                    disabled={documentCount === 0 || hasDocumentErrors}
                />
                <button
                    className="btn btn-default"
                    onClick={handleCancelClick}
                    disabled={saveExternalDocumentsMutation.isLoading}
                >
                    Cancel
                </button>
            </Modal.Footer>
        </Modal>
    );
}
